/**
 * A three-drop email campaign as a report data model instance.
 *
 * Unlike `sample-report.ts`, these are not figures lifted from a real report — email
 * has no published mockup. They are internally exact instead, which is what makes them
 * useful as an acceptance fixture:
 *
 *   summary totals            = Σ per-drop totals            (every field)
 *   clicks 3,035              = mobile 1,755 + non-mobile 1,280
 *   Σ aggregatedLinks.clicks  = 3,035
 *   Σ citiesByClicks.clicks   = 3,035
 *   Σ clicksByDay.clicks      = 3,035
 *   per-drop link totals      = that drop's clicks
 *
 * The summary RATES were produced by `summariseEmail()` in @pdmg/reporting-core, not
 * computed by hand — so a change to the aggregation rule (summing totals, rather than
 * averaging per-drop rates) breaks this fixture, which is the point. Averaging the
 * three drops' CTRs gives 2.08% here too, which is exactly why the distinction is easy
 * to get wrong and worth pinning: the drops are similarly sized, so both methods nearly
 * agree. They would not on a 100-recipient drop next to a 100,000-recipient one.
 *
 * The opt-out figures (238 / 122 / 240, total 600) came from `emailOptOuts()` and are
 * flagged `estimated`. ProData does not send the emails, so opt-outs are unavailable
 * and legacy fabricates them (D44). The values are SEEDED, so they reproduce — under
 * legacy's `_.random()` this fixture could not exist.
 */

const drop = (
  dropNo: number,
  deployDate: string,
  recipients: number,
  opens: number,
  uniqueOpens: number,
  clicks: number,
  uniqueClicks: number,
  bounces: number,
  mobileClicks: number,
  optOuts: number,
  links: Array<[string, number]>,
  cities: Array<[string, number]>
) => ({
  dropNo,
  label: `D#${dropNo}`,
  title: 'Spring Product Launch',
  deployDate,
  recipients,
  opens,
  uniqueOpens,
  clicks,
  uniqueClicks,
  bounces,
  mobileClicks,
  optOuts,
  optOutsEstimated: true,
  ctr: (clicks / recipients) * 100,
  uniqueCtr: (uniqueClicks / recipients) * 100,
  openRate: (opens / recipients) * 100,
  links: links.map(([link, c]) => ({ link, clicks: c, uniqueClicks: null, clickShare: (c / 3035) * 100 })),
  citiesByClicks: cities.map(([city, c]) => ({ city, clicks: c })),
});

export const SAMPLE_EMAIL_REPORT: unknown = {
  dataModelVersion: 1,
  metricsVersion: 'v2',
  lifecycle: 'POSTFLIGHT',
  selection: {
    adSelection: 'delivery-in-window',
    adStatusesIncluded: ['ACTIVE'],
    adGroupSelection: 'delivery-in-window',
    creativesShownLimited: false,
    nonActiveDeliveringAds: 0,
  },
  report: { id: 'r-email-1', version: 1, generatedAt: '2026-04-01T00:00:00Z', periodStart: '2025-03-01', periodEnd: '2025-03-31' },
  campaign: {
    id: 'c-email-1', name: 'Spring Product Launch', reportSo: 'EMAIL1', so: 'EMAIL1', ios: ['IO-E1'],
    clientName: 'Client Sample Report', agencyName: 'YOUR COMPANY NAME',
    startDate: '03/01/2025', endDate: '03/31/2025', channels: ['EMAIL'], bannerAssetId: 'asset-email-creative',
  },

  /**
   * `overall` carries the campaign rollup for blocks shared with other channels.
   * For email, "impressions" ARE recipients — and `ctrBasis` says so explicitly, so a
   * template renders the right footnote instead of the display one.
   */
  overall: {
    delivery: {
      impressions: 145700, retargetImpressions: null,
      clicks: 3035, retargetClicks: null,
      mobileClicks: 1755, desktopClicks: 1280,
    },
    contract: { targetImpressions: 145700 },
    derived: { ctr: 2.083, ctrBasis: 'recipients', overDeliveredImpressions: null, overDeliveryPct: null },
    // Page-1 breakdowns. Mobile device details sum to mobile clicks (1,755); browser type sums to total
    // clicks (3,035). Fixed legacy row order, LG/Htc shown even at low counts.
    devices: [
      { name: 'iPhone', clicks: 1200 }, { name: 'Android', clicks: 480 }, { name: 'Samsung', clicks: 40 },
      { name: 'iPad', clicks: 25 }, { name: 'LG', clicks: 5 }, { name: 'Htc', clicks: 3 }, { name: 'Other', clicks: 2 },
    ],
    browsers: [
      { name: 'Chrome', clicks: 1100 }, { name: 'Safari', clicks: 1800 }, { name: 'Firefox', clicks: 100 },
      { name: 'Edge', clicks: 25 }, { name: 'Other', clicks: 10 },
    ],
  },
  channels: [],
  adGroups: [],
  // The geo targeting page maps the cities recipients clicked from. A real report derives these from
  // `email.citiesByClicks` ("City,ST") in build-email-report; the fixture names them so the sample plots.
  targeting: {
    zips: [],
    cities: ['Boston,MA', 'Providence,RI', 'Worcester,MA', 'Springfield,MA', 'Fall River,MA'],
    geoMode: 'city',
    audience: { notes: [] },
    topSites: [],
  },

  email: {
    subject: 'Introducing the Spring Collection',
    fromName: 'Client Sample Report',
    fromEmail: 'news@example.com',

    drops: [
      drop(1, '2025-03-03', 50000, 9250, 7400, 1180, 940, 620, 690, 238,
        [['https://example.com/offer', 470], ['https://example.com/pricing', 320], ['https://example.com/about', 240], ['https://example.com/contact', 150]],
        [['Boston', 410], ['Providence', 295], ['Worcester', 210], ['Springfield', 145], ['Fall River', 120]]),
      drop(2, '2025-03-10', 48500, 8100, 6480, 995, 810, 540, 570, 122,
        [['https://example.com/offer', 400], ['https://example.com/pricing', 270], ['https://example.com/about', 200], ['https://example.com/contact', 125]],
        [['Boston', 350], ['Providence', 250], ['Worcester', 175], ['Springfield', 125], ['Fall River', 95]]),
      drop(3, '2025-03-17', 47200, 7300, 5900, 860, 690, 480, 495, 240,
        [['https://example.com/offer', 345], ['https://example.com/pricing', 230], ['https://example.com/about', 170], ['https://example.com/contact', 115]],
        [['Boston', 300], ['Providence', 215], ['Worcester', 155], ['Springfield', 105], ['Fall River', 85]]),
    ],

    // Σ of the three drops, field by field.
    recipients: 145700,
    clicks: 3035,
    uniqueClicks: 2440,
    opens: 24650,
    uniqueOpens: 19780,
    bounces: 1640,
    mobileClicks: 1755,
    nonMobileClicks: 1280,
    optOuts: 600,
    optOutsEstimatedCount: 3,

    // From summariseEmail() — computed on summed totals, never averaged.
    ctr: 2.0830,
    uniqueCtr: 1.6747,
    openRate: 16.9183,
    uniqueOpenRate: 13.5758,
    bounceRate: 1.1256,
    htmlCtr: 12.3124,

    aggregatedLinks: [
      { link: 'https://example.com/offer', clicks: 1215, uniqueClicks: null, clickShare: 40.0329 },
      { link: 'https://example.com/pricing', clicks: 820, uniqueClicks: null, clickShare: 27.0181 },
      { link: 'https://example.com/about', clicks: 610, uniqueClicks: null, clickShare: 20.0989 },
      { link: 'https://example.com/contact', clicks: 390, uniqueClicks: null, clickShare: 12.8501 },
    ],
    clicksByDay: [
      { date: '2025-03-03', clicks: 780 }, { date: '2025-03-04', clicks: 400 },
      { date: '2025-03-10', clicks: 660 }, { date: '2025-03-11', clicks: 335 },
      { date: '2025-03-17', clicks: 570 }, { date: '2025-03-18', clicks: 290 },
    ],
    citiesByClicks: [
      { city: 'Boston', clicks: 1060 }, { city: 'Providence', clicks: 760 },
      { city: 'Worcester', clicks: 540 }, { city: 'Springfield', clicks: 375 },
      { city: 'Fall River', clicks: 300 },
    ],
  },

  /**
   * Every drop's opt-out figure is estimated, so this report DOES contain adjusted
   * data — and the ProvenanceNote must render. An email report that silently presents
   * a fabricated opt-out count as measured is the exact thing R2/R6 exist to prevent.
   */
  /*
   * Presentation defaults, spelled out rather than left to the schema.
   *
   * A fixture that relies on Zod's defaults renders correctly and tests nothing: the
   * flags would be absent from the object the template actually reads, so a binding
   * against one would resolve to null and the block would quietly disappear.
   */
  presentation: {
    /*
     * ON for this fixture, and the schema default is OFF.
     *
     * The legacy drawer ships SHOW CITIES CHART unchecked, so `false` is the right
     * default for a new report. This particular sample is the one the block suite
     * exercises the pie charts through, and a fixture that turned them off would leave
     * four charts in the catalog with nothing rendering them.
     */
    showCitiesChart: true,
    // On here so the catalog/sample exercises the optional geo targeting page; production defaults it OFF.
    showGeoTargeting: true,
  },
  provenance: {
    overrideCount: 0,
    overridesByKind: { DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 },
    hasAdjustedFigures: true,
  },
};
